import { FormEvent, useState } from 'react'
import { Add, ContentCopy, Help } from '@nine-thirty-five/material-symbols-react/sharp'
import { Button, Dialog, IconButton, List, ListItem, ListItemText, TextField, Tooltip } from '@iziplay/ui-kit'

import Spinner from './spinner'
import { displayNotification } from '../state/ui'
import { KoboDevice, koboEndpointURL, problemDetail, useAddKoboDevice, useKoboDevices } from '../queries/books'

function copy(text: string) {
  navigator.clipboard
    .writeText(text)
    .then(() => displayNotification('Copied to clipboard', 'success'))
    .catch(() => displayNotification('Failed to copy', 'error'))
}

/*
 * Step-by-step Kobo configuration for one device, shown right after
 * creating it and reachable again from each row.
 */
function SetupDialog({ device, onClose }: { device: KoboDevice | null; onClose: () => void }) {
  const endpointLine = device === null ? '' : `api_endpoint=${koboEndpointURL(device.id)}`

  return (
    <Dialog
      open={device !== null}
      onClose={onClose}
      title={`Set up "${device?.nickname ?? ''}"`}
      actions={<Button variant="text" onClick={onClose}>Done</Button>}
    >
      <p style={{ margin: 0 }}>
        The endpoint below is this device&apos;s only credential, treat it like a password.
      </p>
      <ol style={{ margin: 0, paddingLeft: '1.4em', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <li>
          Best results start clean: factory reset the reader (Settings → Device information →
          Reset), then during its setup create a <strong>new, empty</strong> Kobo/Rakuten account
          and sign in with it. An account with purchases would mix its real library into this one.
        </li>
        <li>Connect the Kobo to your computer with its USB cable and accept the connection on the device.</li>
        <li>
          On the drive that appears, open the hidden <code>.kobo</code> folder, then open{' '}
          <code>Kobo/Kobo eReader.conf</code> in a text editor.
        </li>
        <li>
          In the <code>[OneStoreServices]</code> section (add the section if it is missing), set:
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              margin: '6px 0 0',
              padding: '4px 4px 4px 10px',
              borderRadius: '4px',
              background: 'var(--light-gray)'
            }}
          >
            <code style={{ flex: 1, fontSize: '0.85em', wordBreak: 'break-all' }}>{endpointLine}</code>
            <Tooltip title="Copy">
              <IconButton onClick={() => copy(endpointLine)} aria-label="Copy endpoint line">
                <ContentCopy size={18} />
              </IconButton>
            </Tooltip>
          </div>
        </li>
        <li>
          Save the file and eject the Kobo; your books then appear in its library on every sync.
        </li>
      </ol>
    </Dialog>
  )
}

function AddDeviceDialog({
  open,
  onClose,
  onCreated
}: {
  open: boolean
  onClose: () => void
  onCreated: (device: KoboDevice) => void
}) {
  const addDevice = useAddKoboDevice()
  const [nickname, setNickname] = useState('')

  function submit(event: FormEvent) {
    event.preventDefault()
    if (nickname.trim() === '') return

    addDevice.mutate(nickname.trim(), {
      onSuccess: device => {
        setNickname('')
        onCreated(device)
      },
      onError: err => displayNotification(problemDetail(err, 'Failed to add the device'), 'error')
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add a Kobo e-reader"
      actions={
        <>
          <Button variant="text" onClick={onClose}>Cancel</Button>
          <Button
            variant="text"
            type="submit"
            form="add-kobo-form"
            disabled={nickname.trim() === '' || addDevice.isPending}
          >
            Add
          </Button>
        </>
      }
    >
      <p style={{ margin: 0 }}>
        Give the device a nickname; the configuration steps come right after.
      </p>
      <form id="add-kobo-form" onSubmit={submit}>
        <TextField
          id="kobo-nickname"
          label="Nickname"
          placeholder="Living room Kobo"
          value={nickname}
          autoFocus
          onChange={e => setNickname(e.target.value)}
        />
      </form>
    </Dialog>
  )
}

export function KoboDevices() {
  const devices = useKoboDevices()
  const [addOpen, setAddOpen] = useState(false)
  const [setupDevice, setSetupDevice] = useState<KoboDevice | null>(null)

  return (
    <div>
      <p>
        Books you add sync to the library of every Kobo e-reader registered here.
      </p>
      {devices.isPending ? (
        <Spinner />
      ) : devices.error ? (
        <p>Failed to load your devices.</p>
      ) : (
        <List>
          {devices.data.map(device => (
            <ListItem
              key={device.id}
              secondaryAction={
                <>
                  <Tooltip title="Setup instructions">
                    <IconButton onClick={() => setSetupDevice(device)} aria-label="Setup instructions">
                      <Help size={20} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy API endpoint">
                    <IconButton onClick={() => copy(koboEndpointURL(device.id))} aria-label="Copy API endpoint">
                      <ContentCopy size={20} />
                    </IconButton>
                  </Tooltip>
                </>
              }
            >
              <ListItemText
                primary={device.nickname}
                secondary={[
                  device.model,
                  device.serialNumber,
                  device.osVersion && `OS ${device.osVersion}`
                ]
                  .filter(Boolean)
                  .join(' · ') || 'Not seen yet'}
              />
            </ListItem>
          ))}
          {devices.data.length === 0 && (
            <ListItem>
              <ListItemText primary="No device yet" secondary="Register one below" />
            </ListItem>
          )}
        </List>
      )}
      <Button variant="text" startIcon={<Add size={24} />} onClick={() => setAddOpen(true)}>
        Add device
      </Button>
      <AddDeviceDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={device => {
          setAddOpen(false)
          setSetupDevice(device)
        }}
      />
      <SetupDialog device={setupDevice} onClose={() => setSetupDevice(null)} />
    </div>
  )
}
